import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import StyledSelect from "../styled/StyledSelect";
import { useSignUpRetrieveBillingRequestFlow } from "../../api/auth.api";
import { Button } from "../ui/button";
import { RegisterFormData } from "../../types/forms/auth/registerFormData.form";
import { useSearchParams } from "react-router-dom";
import StyledStepper from "../styled/StyledStepper";
import { Check } from "lucide-react";
import clsx from "clsx";
import { usePaymentMethodOptions } from "@/hooks/useEnumOptions";

interface RegisterPaymentStepProps {
  onSubmit: () => void;
  onBack: () => void;
}

const RegisterStepPayment: React.FC<RegisterPaymentStepProps> = ({ onSubmit, onBack }) => {
  const paymentMethodOptions = usePaymentMethodOptions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [acceptedCGU, setAcceptedCGU] = useState(false);

  const { control, reset, watch, setValue } = useFormContext<RegisterFormData>();

  const formData = watch();

  const { mutateAsync: signUpRetrieveBillingRequestFlow } = useSignUpRetrieveBillingRequestFlow();

  const mandateAuthorized = JSON.parse(searchParams.get("mandateAuthorized") || "false");
  const registerActiveStep = JSON.parse(localStorage.getItem("registerActiveStep") || "0");
  const [activeStep, _setActiveStep] = useState<number>(Number(registerActiveStep) || 0);

  useEffect(() => {
    if (searchParams.has("mandateAuthorized")) {
      if (mandateAuthorized) {
        setActiveStep(activeStep + 1);
      }
      searchParams.delete("mandateAuthorized");
      setSearchParams(searchParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mandateAuthorized]);

  const setActiveStep = (value: number) => {
    _setActiveStep(value);
    localStorage.setItem("registerActiveStep", JSON.stringify(value));
  };

  useEffect(() => {
    const formData = localStorage.getItem("registerFormData");
    if (formData) {
      reset(JSON.parse(formData));
    }
  }, [reset]);

  const handleContinue = async (index: number) => {
    const inputData = formData.billingEntities[index].paymentMethod;

    if (inputData?.value === "CARD" || inputData?.value === "VIREMENT") {
      setActiveStep(activeStep + 1);
    } else if (inputData?.value === "PRELEVEMENT") {
      const response = await signUpRetrieveBillingRequestFlow({
        customerId: formData.customerId,
        givenName: formData.billingEntities[index].companyName,
        familyName: formData.billingEntities[index].companyName,
        email: formData.billingEntities[index].email,
        addressLine1: formData.billingEntities[index].street,
        city: formData.billingEntities[index].city,
        postalCode: formData.billingEntities[index].postalCode,
        countryCode: "FR",
        token: searchParams.get("token") as string,
      });

      setValue(`billingEntities.${index}.billingRequestId`, response.data.billingRequestId);

      localStorage.setItem("registerFormData", JSON.stringify(formData));
      window.location.href = response.data.authorisationUrl;
    }
  };

  return (
    <div className="flex flex-col gap-8 py-28 md:px-20 lg:px-8">
      <div className="flex flex-col gap-2">
        <h2 className="font-jakarta-sans text-center text-3xl font-semibold text-black">
          Créer votre compte
        </h2>
        <p className="text-center text-gray-600">
          Entrez vos informations pour créer votre espace.
        </p>
      </div>
      <StyledStepper
        activeStep={activeStep}
        stepsContent={formData.billingEntities.map((billingEntity, index) => (
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between rounded-lg border-2 border-gray-300 bg-gray-50 p-4">
              <div>
                <p
                  className={clsx("text-sm", {
                    "text-gray-900": index <= activeStep,
                    "text-gray-500": index > activeStep,
                  })}
                >
                  Entité de facturation {index === 0 ? "par défaut" : index + 1}
                </p>
                <p
                  className={clsx("font-medium", {
                    "text-gray-900": index <= activeStep,
                    "text-gray-500": index > activeStep,
                  })}
                >
                  {billingEntity.companyName}
                </p>
              </div>
              {billingEntity.paymentMethod?.value === "PRELEVEMENT" && activeStep !== index && (
                <div className="flex flex-row items-center gap-1 rounded-xl border border-green-200 bg-green-50 px-1.5 py-0.5">
                  <Check className="size-4 text-green-600" />
                  <p className="text-green-700">Prélèvement</p>
                </div>
              )}
            </div>

            {activeStep === index && (
              <div className="flex flex-col gap-4 sm:flex-row">
                <StyledSelect
                  title="Mode de paiement"
                  control={control}
                  name={`billingEntities.${index}.paymentMethod`}
                  options={paymentMethodOptions}
                  className="w-full text-xs sm:text-sm"
                />
                <div className="flex flex-1 content-center py-1">
                  <Button
                    onClick={() => handleContinue(index)}
                    className="w-full"
                    disabled={!formData.billingEntities[index].paymentMethod}
                  >
                    Continuer
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}
      />
      <div className="flex flex-row gap-2">
        <label className="">
          <input
            type="checkbox"
            className="peer w-[16px]"
            checked={acceptedCGU}
            onClick={() => setAcceptedCGU(!acceptedCGU)}
          />{" "}
          Je reconnais avoir pris connaissance et accepté les
          <a href="/cgu.html" target="_blank">
            {" "}
            Conditions Générales d'Utilisation
          </a>{" "}
          de Pelican Air Services.
        </label>
      </div>

      <div className="flex flex-row justify-end gap-2">
        <Button variant="secondary" onClick={onBack}>
          Retour
        </Button>
        <Button
          disabled={activeStep != formData.billingEntities.length || !acceptedCGU}
          onClick={onSubmit}
        >
          Suivant
        </Button>
      </div>
    </div>
  );
};

export default RegisterStepPayment;
